import { withoutOversizedSearch, SEARCH_LENGTH_MESSAGE } from "@/helpers/searchLimit";
import { useToast } from "vue-toastification";
import { createRouter, createWebHistory } from "vue-router";

import api from "../api";
import Socket from "../plugins/socket";
import { getCountryFromService } from "@/helpers/phone";
import Account from "../api/account";
import { addToRouterHistory } from "@/helpers/router";
import { canonicalizePathFormShareLink, parseShareLinkLocation } from "@/helpers/shareLink";
import { isWhatsNewEnabled } from "@/helpers/isWhatsNewEnabled";
import { isReportsEnabled } from "@/helpers/isReportsEnabled";
import { loadMeFeatureFlag } from "@/helpers/meFeatureFlags";
import { auth } from "../auth";
import { setDefaultPhoneCountry } from "@/helpers/utilities";
import { logError, logInfo, logWarn } from "@/helpers/logError";
import { isTransientAuthTokenError } from "@/types/errors/TransientAuthTokenError";
import { isUnauthenticatedError } from "@/types/errors/UnauthenticatedError";
import { isAuth0RedirectSuppressedError } from "@/types/errors/Auth0RedirectSuppressedError";
import { isOrganizationSwitchNotPerformedError } from "@/types/errors/OrganizationSwitchNotPerformedError";
function getCurrentOrgId() {
	return localStorage.getItem("contact:currentOrgId");
}

let isLoaded = false;
let currentAccount = null;
let deferredBootAccountRouteFullPath = null;
const TERMINAL_GET_ME_AUTH_ERROR_CODES = new Set(["INVALID_TOKEN", "TOKEN_INVALID", "UNAUTHORIZED", "TOKEN_EXPIRED"]);
const getGetMeAuthErrorCode = (error) => (typeof error?.errorCode === "string" ? error.errorCode.trim().toUpperCase() : "");
const isTerminalGetMeAuthError = (error) => TERMINAL_GET_ME_AUTH_ERROR_CODES.has(getGetMeAuthErrorCode(error));
const buildInstructionCreateReturnTo = (path, query) => {
	const searchParams = new URLSearchParams();
	for (const key of ["from", "projectId"]) {
		if (typeof query[key] === "string") {
			searchParams.set(key, query[key]);
		}
	}
	const search = searchParams.toString();
	return search ? `${path}?${search}` : path;
};
const BOOT_ACCOUNT_NETWORK_MESSAGE_FRAGMENTS = [
	"failed to fetch",
	"load failed",
	"networkerror when attempting to fetch",
	"request did not go through",
	"appear to be offline"
];
const hasHttpFailureStatus = (error) => {
	if (!error || typeof error !== "object") {
		return false;
	}
	const statusCandidates = [error.status, error.statusCode, error.response?.status, error.code];
	return statusCandidates.some((status) => typeof status === "number" && status >= 400 && status <= 599);
};

export const isNetworkShapedBootAccountLoadError = (error) => {
	if (hasHttpFailureStatus(error)) {
		return false;
	}
	if (!(error instanceof TypeError)) {
		return false;
	}
	const message = error.message.toLowerCase();
	return BOOT_ACCOUNT_NETWORK_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment));
};

// LSP-17079: a deferred boot deep-link is replayed by retryDeferredBootAccountNavigation. The replay
// re-enters beforeEach, and if getMe flaps AGAIN there the route is re-deferred — but App.vue's boot
// retry has already finished, so nothing would replay it again and the deep link would strand. These
// triggers re-attempt the replay on connectivity/return (+ a bounded timer for soft flaps that never
// emit `online`) until the navigation actually lands (the stored path clears). One-shot + self-disarm,
// re-armed by retryDeferredBootAccountNavigation's finally only while a path remains deferred.
const DEFERRED_BOOT_NAV_RETRY_MS = 5000;
// LSP-18866: the authenticated-boot org reconcile defers and replays on a transient failure. That
// replay must be BOUNDED. An org switch that keeps failing — a degraded-but-not-down Auth0, a claim
// that stays unreadable — would otherwise retry every 5s for the life of the tab, showing the user
// nothing and never reaching the terminal dialog. Five attempts spans roughly 25s of the flapping
// this is meant to ride out; past that the failure is not transient in any useful sense and the
// user is better served by being told. Scoped to the org-switch path ONLY: the sibling getMe
// deferrals (LSP-17079) are driven by App.vue's own retry schedule and are deliberately unbounded,
// because there the app genuinely cannot proceed without connectivity and has a boot error message
// on screen the whole time.
const MAX_BOOT_ORG_SWITCH_ATTEMPTS = 5;
let bootOrgSwitchAttempts = 0;
let deferredBootNavRetryArmed = false;
let deferredBootNavRetryTimer = null;
// Held at module scope so disarmDeferredBootNavRetry can detach the listeners even when it is called
// (e.g. from resetRouterAccountContext on an org switch) before `fire` has run — otherwise a stale
// online/pageshow handler would survive the reset and later consume a freshly deferred boot path,
// racing the intentional replay (LSP-17079).
let deferredBootNavRetryHandler = null;
function disarmDeferredBootNavRetry() {
	deferredBootNavRetryArmed = false;
	if (deferredBootNavRetryTimer !== null) {
		clearTimeout(deferredBootNavRetryTimer);
		deferredBootNavRetryTimer = null;
	}
	if (deferredBootNavRetryHandler !== null && typeof window !== "undefined") {
		window.removeEventListener("online", deferredBootNavRetryHandler);
		window.removeEventListener("pageshow", deferredBootNavRetryHandler);
	}
	deferredBootNavRetryHandler = null;
}
function armDeferredBootNavRetryTriggers(routerInstance) {
	if (deferredBootNavRetryArmed || typeof window === "undefined") {
		return;
	}
	deferredBootNavRetryArmed = true;
	const fire = (event) => {
		// event.type distinguishes the DOM triggers; the bounded timer invokes fire() with no arg.
		// Log which trigger drove the replay so a future stranded-deep-link recurrence is traceable
		// in Datadog instead of requiring reconstruction (CLAUDE.md silent-failure logging mandate).
		const trigger = event && event.type ? event.type : "timer";
		// review 5023486490 #7: only a PERSISTED pageshow is the "user came back to this page"
		// signal these triggers are for. Every ordinary page load also fires a pageshow with
		// persisted:false, and when a deferral is armed during the router's INITIAL navigation
		// (the org-switch catch does exactly that) that load-time event arrives first and burned
		// the one-shot trigger on a non-event — spending a replay attempt while the failure was
		// still present. Ignore it and stay armed; the listener is deliberately not `once` so
		// staying armed is possible.
		if (trigger === "pageshow" && event.persisted !== true) {
			logInfo("Router: ignored a non-persisted pageshow for the deferred boot retry; staying armed", {
				path: deferredBootAccountRouteFullPath
			});
			return;
		}
		logInfo("Router: deferred boot account navigation retry trigger fired", {
			trigger,
			path: deferredBootAccountRouteFullPath
		});
		// disarmDeferredBootNavRetry removes both listeners and clears the timer, so we don't remove
		// them here — that keeps teardown in one place and correct whether fired or reset.
		disarmDeferredBootNavRetry();
		retryDeferredBootAccountNavigation(routerInstance);
	};
	deferredBootNavRetryHandler = fire;
	window.addEventListener("online", fire, { once: true });
	// NOT `once`: fire() ignores a non-persisted pageshow (see above) and must remain registered
	// to catch the real bfcache return later. disarmDeferredBootNavRetry removes it explicitly.
	window.addEventListener("pageshow", fire);
	deferredBootNavRetryTimer = setTimeout(fire, DEFERRED_BOOT_NAV_RETRY_MS);
}

// LSP-18866: the ONE place the authenticated-boot org reconcile defers and re-arms its replay, so
// the attempt budget cannot be enforced on one branch and forgotten on the sibling — which is the
// failure mode that recurred through this PR. Both transient causes (a suppressed redirect, and a
// genuine transient/not-performed failure) come through here.
//
// At exhaustion it stops deferring and opens the terminal dialog instead. That dialog is the only
// user-visible signal on this path; retrying silently forever is the behaviour this bound exists to
// prevent.
function deferBootOrgSwitchRetry(to, reason, organizationId) {
	bootOrgSwitchAttempts += 1;
	if (bootOrgSwitchAttempts >= MAX_BOOT_ORG_SWITCH_ATTEMPTS) {
		logError("Router: boot org switch kept failing; giving up and telling the user", new Error("BootOrgSwitchRetriesExhausted"), {
			file: "router/index.js",
			reason,
			organizationId,
			routeName: to.name,
			path: to.fullPath,
			attempts: bootOrgSwitchAttempts,
			maxAttempts: MAX_BOOT_ORG_SWITCH_ATTEMPTS,
			classification: "terminal",
			retryable: false
		});
		deferredBootAccountRouteFullPath = null;
		disarmDeferredBootNavRetry();
		api.openNoAccountDialog(
			"org_access_lost",
			"We couldn't switch you to your organization after several attempts, so we've stopped rather than show you another organization's data. Please log in again."
		);
		return;
	}
	deferredBootAccountRouteFullPath = to.fullPath;
	armDeferredBootNavRetryTriggers(router);
	logInfo("Router beforeEach: transient boot org-switch failure; navigation deferred and replay armed", {
		file: "router/index.js",
		reason,
		organizationId,
		routeName: to.name,
		path: to.fullPath,
		classification: "transient",
		retryable: true,
		attempts: bootOrgSwitchAttempts,
		maxAttempts: MAX_BOOT_ORG_SWITCH_ATTEMPTS,
		replayArmedBy: "router-org-switch-catch"
	});
}

export function resetRouterAccountContext() {
	isLoaded = false;
	currentAccount = null;
	deferredBootAccountRouteFullPath = null;
	bootOrgSwitchAttempts = 0;
	disarmDeferredBootNavRetry();
}

export function retryDeferredBootAccountNavigation(routerInstance = router) {
	if (!deferredBootAccountRouteFullPath || !routerInstance?.replace) {
		return false;
	}

	// Clear first so beforeEach's own re-store (on a fresh transient failure during the replay) is the
	// only thing that can leave a path behind — that's how we detect a re-deferral in `finally`.
	const routeFullPath = deferredBootAccountRouteFullPath;
	deferredBootAccountRouteFullPath = null;
	void routerInstance
		.replace(routeFullPath)
		.catch((error) => {
			logInfo("Router beforeEach: deferred boot account navigation retry failed", {
				path: routeFullPath,
				error
			});
		})
		.finally(() => {
			// If beforeEach re-deferred during the replay, deferredBootAccountRouteFullPath was re-stored;
			// keep replaying on connectivity/return so the deep link is never stranded. If it landed
			// (path cleared and not re-stored), leave the triggers disarmed. Both branches log so the
			// retry outcome is observable in Datadog (CLAUDE.md silent-failure logging mandate).
			if (deferredBootAccountRouteFullPath) {
				logInfo("Router: deferred boot account navigation re-deferred during replay; re-arming retry", {
					path: deferredBootAccountRouteFullPath
				});
				armDeferredBootNavRetryTriggers(routerInstance);
			} else {
				logInfo("Router: deferred boot account navigation landed", { path: routeFullPath });
			}
		});
	return true;
}

const HomePage = () => import("../components/HomePage.vue");
const LoadingPage = () => import("../components/LoadingPage.vue");
const CustomersPage = () => import("../components/CustomersPage.vue");
const CustomerPage = () => import("../components/CustomerPage.vue");
const UsersPage = () => import("../components/UsersPage.vue");
const UserPage = () => import("../components/UserPage.vue");
const SettingsPage = () => import("../components/SettingsPage.vue");
const OrganizationDetails = () => import("../components/settings/OrganizationDetails.vue");
const CallSettings = () => import("../components/settings/CallSettings.vue");
const ContentAndNotificationSettings = () => import("../components/settings/ContentAndNotificationSettings.vue");
const GoogleReviewSettings = () => import("../components/settings/GoogleReviewSettings.vue");
const TagList = () => import("../components/tags/TagList.vue");
const ChecklistsSettings = () => import("../components/settings/ChecklistsSettings.vue");
const InstructionsSettings = () => import("../components/settings/InstructionsSettings.vue");
const LiveCallFormsSettings = () => import("../components/settings/LiveCallFormsSettings.vue");
const ConversationsPage = () => import("../components/ConversationsPage.vue");
const ProjectsPage = () => import("../components/ProjectsPage.vue");
const ReportsPage = () => import("../components/ReportsPage.vue");
const ReviewsPage = () => import("../components/ReviewsPage.vue");
const EntityDetailPage = () => import("../components/EntityDetailPage.vue");
const SubscriptionPage = () => import("../components/SubscriptionPage.vue");
const PricingPage = () => import("../components/PricingPage.vue");
const PaymentSuccess = () => import("../components/PaymentSuccess.vue");
const AttributesPanel = () => import("../components/AttributesPanel.vue");
const ActionUploadPhoto = () => import("../components/ActionUploadPhoto.vue");
const ActionPhoto = () => import("../components/ActionPhoto.vue");
const ActionStartRecording = () => import("../components/ActionStartRecording.vue");
const ActionVideo = () => import("../components/ActionVideo.vue");
const ActionSchedule = () => import("../components/ActionSchedule.vue");
const AppStorePage = () => import("../components/AppStorePage.vue");
const NotificationsPage = () => import("../components/NotificationsPage.vue");
const SearchResults = () => import("../components/search/SearchResults.vue");
const EntityResultsPage = () => import("../components/search/EntityResultsPage.vue");
const ReplaceUserPage = () => import("../components/ReplaceUserPage.vue");
const ChecklistEditor = () => import("../components/ChecklistEditor.vue");
const InstructionEditor = () => import("../components/InstructionEditor.vue");
const LiveCallFormEditor = () => import("../components/LiveCallFormEditor.vue");
const TemplatesPage = () => import("../components/scribe/TemplatesPage.vue");
const TemplateEditPage = () => import("../components/scribe/TemplateEditPage.vue");
const TemplateDetailsPage = () => import("../components/scribe/TemplateDetailsPage.vue");
const FormsPage = () => import("../components/FormsPage.vue");
const FormPage = () => import("../components/FormPage.vue");
const VideosPage = () => import("../components/VideosPage.vue");
const DataPrivacyPage = () => import("../components/DataPrivacyPage.vue");
const WhatsNewPage = () => import("../components/WhatsNewPage.vue");
const SheetView = () => import("../components/SheetView.vue");
const ClaimSharePage = () => import("../components/ClaimSharePage.vue");
const DevToolsUploadsPage = () => import("../components/DevToolsUploadsPage.vue");
const MobileUploadsFallback = () => import("../components/MobileUploadsFallback.vue");
const MobileRecordFallback = () => import("../components/MobileRecordFallback.vue");

// Vue.use(Router);

// Helper to detect share-link routes (project URL with shareId query param)
function isShareLinkRoute(to) {
	return (to.path.indexOf("projects/") !== -1 || to.path.indexOf("projects2/") !== -1) && to.fullPath.indexOf("shareId") !== -1;
}

function isStaleSettingsRoute(route) {
	if (!route.path?.startsWith("/settings/")) {
		return false;
	}

	const routeOrganizationId = typeof route.params?.id === "string" ? route.params.id : null;
	const currentOrganizationId = getCurrentOrgId();
	return !!routeOrganizationId && !!currentOrganizationId && routeOrganizationId !== currentOrganizationId;
}

function hardNavigateStaleSettingsRoute(route) {
	if (!isStaleSettingsRoute(route)) {
		return false;
	}

	// A second tab can keep stale in-memory account state after another tab switches
	// organizations. Leave the stale settings URL so the app boots in the current org.
	window.location.href = "/";
	return true;
}

// Same shape, but accepts a raw path string (e.g., the post-auth appState.path
// returned by Auth0). Used to validate caller-supplied paths before navigating —
// we restore share links specifically because their access is via signed shareId,
// not org membership, so the org-switch flakeyness that drove the always-/ rule
// doesn't apply.
function isShareLinkPath(path) {
	if (!path || typeof path !== "string") return false;
	// LSP-17770: a per-recipient claim link (/share/:token) is also signed access —
	// restore it after Auth0 so a recipient who logs in mid-claim returns to the
	// claim page instead of being dropped on home.
	if (path.startsWith("/share/")) return true;
	if (!path.startsWith("/projects/") && !path.startsWith("/projects2/")) return false;
	return /[?&]shareId=/.test(path);
}

// Helper function to safely access auth instance
function getAuthInstance() {
	// Try to use router.app.$auth if available, otherwise fallback to direct auth instance
	// if (router && router.app && router.app.$auth) {
	// 	return router.app.$auth;
	// }
	return auth;
}

const HASH_SCROLL_DELAY_MS = 500;

// ignore errors on re-navigate
// const originalPush = Router.prototype.push;
// Router.prototype.push = function push(location) {
// 	return originalPush.call(this, location).catch((err) => err);
// };

// LSP-20890: rewrite a path-form project share link in the address bar before the router (and App.vue,
// which reads window.location at boot to mount the signed-out public project view) ever sees it. The
// `project-shared` record redirect below still covers in-app navigations to the path form.
function canonicalizeInitialPathFormShareLink() {
	if (typeof window === "undefined") return;
	try {
		const canonicalPath = canonicalizePathFormShareLink(window.location);
		if (!canonicalPath) return;
		window.history.replaceState(window.history.state, "", canonicalPath);
		logInfo("Router: rewrote a path-form project share link to the ?shareId= form before boot", {
			projectId: parseShareLinkLocation(canonicalPath).projectId
		});
	} catch (error) {
		// Not fatal: the record redirect still rewrites the route, only the pre-boot address bar is stale.
		logWarn("Router: could not rewrite a path-form project share link before boot", {
			errorName: error?.name,
			message: error?.message
		});
	}
}
canonicalizeInitialPathFormShareLink();

const router = createRouter({
	history: createWebHistory(),
	scrollBehavior(to, from, savedPosition) {
		if (to.hash) {
			// https://router.vuejs.org/guide/advanced/scroll-behavior.html#Delaying-the-scroll
			return new Promise((resolve) =>
				setTimeout(() => {
					// Only scroll when the hash target actually exists at resolve time. Returning
					// { el } for a missing element makes Vue Router log "Couldn't find element using
					// selector ..." — and because this resolve is delayed, that warn can surface in an
					// unrelated unit test's afterEach window (cross-test flake). The try/catch guards a
					// hash that is not a valid CSS selector. Falling back to no scroll is also correct in
					// prod when a hash target never renders.
					let targetExists = false;
					try {
						targetExists = !!document.querySelector(to.hash);
					} catch {
						targetExists = false;
					}
					resolve(targetExists ? { el: to.hash, behavior: "smooth" } : { left: 0, top: 0 });
				}, HASH_SCROLL_DELAY_MS)
			);
		}
		if (savedPosition) {
			return savedPosition;
		}
		return { left: 0, top: 0 };
	},
	routes: [
		{
			path: "/",
			name: "home",
			component: HomePage
		},
		{
			path: "/sign-in",
			name: "sign-in",
			component: LoadingPage,
			meta: { public: true }
		},
		{
			path: "/callback",
			name: "callback",
			component: LoadingPage,
			meta: { public: true }
		},
		{
			path: "/payment-success",
			name: "paymentSuccess",
			component: PaymentSuccess
		},
		{
			path: "/logout",
			name: "logout",
			component: HomePage,
			meta: { public: true }
		},
		{
			path: "/contacts",
			name: "contacts",
			component: CustomersPage
		},
		{
			path: "/contacts/:id",
			name: "contact",
			meta: {
				entityTableName: "customers",
				orgScoped: true,
				orgListPath: "/contacts"
			},
			components: {
				default: CustomerPage,
				inner: AttributesPanel
			}
		},
		{
			// LSP-15935: the user menu hid the "Users" link for non-admin roles,
			// but the route itself had no guard — typing /users into the URL
			// loaded the page (the API blocked writes, but the list and details
			// were still visible). Mirror the requiredRoles pattern used by
			// /settings sub-routes, /forms, and /templates so the router's
			// beforeEach redirects User-role accounts to home.
			path: "/users",
			name: "users",
			component: UsersPage,
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"]
			}
		},
		{
			path: "/users/:id",
			name: "user",
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"],
				orgScoped: true,
				orgListPath: "/users"
			},
			component: UserPage
		},
		{
			path: "/users/replace/:id",
			name: "replaceUser",
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"],
				orgScoped: true,
				orgListPath: "/users"
			},
			component: ReplaceUserPage
		},
		{
			// Active org is read from the auth-session token, not the URL.
			// orgScoped applies to every child too — useOrgRouteGuard sees a
			// /settings/<section> route as orgScoped via inherited meta.
			path: "/settings",
			name: "settings",
			meta: { orgScoped: true, orgListPath: "/settings" },
			component: SettingsPage,
			children: [
				{
					path: "organization-details",
					name: "settings-organization-details",
					component: OrganizationDetails,
					meta: {
						requiredRoles: ["Administrator", "Super Administrator", "Support"]
					}
				},
				{
					path: "call-settings",
					name: "settings-call-settings",
					component: CallSettings,
					meta: {
						requiredRoles: ["Administrator", "Super Administrator", "Support"]
					}
				},
				{
					path: "content-notification-settings",
					name: "settings-content-notification-settings",
					component: ContentAndNotificationSettings,
					meta: {
						requiredRoles: ["Administrator", "Super Administrator", "Support"]
					}
				},
				{
					path: "google-review-settings",
					name: "settings-google-review-settings",
					component: GoogleReviewSettings,
					meta: {
						requiredRoles: ["Administrator", "Super Administrator", "Support"]
					}
				},
				{
					path: "tags",
					name: "settings-tags",
					component: TagList,
					meta: {
						requiredRoles: ["Administrator", "Super Administrator", "Support"]
					}
				},
				{
					// LSP-15604: the Apps section moved out of Settings to the top-level
					// "Integrations" sidebar item. Keep this redirect so old bookmarks and
					// links to /settings/app-store land on the page instead of an empty tile.
					// Return an object (not a string) so query + hash survive — Vue Router 4
					// drops both on a plain-string redirect (same pattern as the legacy
					// redirects below).
					path: "app-store",
					redirect: (to) => ({ path: "/integrations", query: to.query, hash: to.hash })
				},
				{
					path: "checklist-templates",
					name: "settings-checklist-templates",
					component: ChecklistsSettings,
					meta: {
						requiredRoles: ["Administrator", "Super Administrator", "Support"]
					}
				},
				{
					path: "instruction-templates",
					name: "settings-instruction-templates",
					component: InstructionsSettings,
					meta: {
						requiredRoles: ["Administrator", "Super Administrator", "Support"]
					}
				},
				{
					path: "live-call-forms",
					name: "settings-live-call-forms",
					component: LiveCallFormsSettings,
					meta: {
						requiredRoles: ["Administrator", "Super Administrator", "Support"]
					}
				},
				{
					// Typo catch-all — land on the tile grid, not the global not-found.
					// Returns an object (not a string) so query + hash survive the
					// redirect, matching the legacy redirects below.
					path: ":section",
					redirect: (to) => ({ path: "/settings", query: to.query, hash: to.hash })
				}
			]
		},
		// Back-compat redirect: legacy /settings/:id and /organizations/:id URLs.
		// Returning an object (not a string) preserves query + hash. Vue Router 4
		// drops both when a redirect resolver returns a plain string.
		{
			path: "/settings/:id/:section?",
			redirect: (to) => ({
				path: `/settings${to.params.section ? `/${to.params.section}` : ""}`,
				query: to.query,
				hash: to.hash
			})
		},
		{
			path: "/organizations/:id/:section?",
			redirect: (to) => ({
				path: `/settings${to.params.section ? `/${to.params.section}` : ""}`,
				query: to.query,
				hash: to.hash
			})
		},
		{
			path: "/subscriptions/:id",
			name: "subscription",
			// Subscription is keyed by org id; on org switch, fall back to home
			// rather than to the new org's /subscriptions/<orgId> URL (the user
			// may not have permission to view it).
			meta: { orgScoped: true, orgListPath: "/" },
			component: SubscriptionPage
		},
		{
			path: "/pricing",
			name: "pricing",
			component: PricingPage
		},
		{
			path: "/conversations",
			name: "conversations",
			components: {
				default: ConversationsPage
			}
		},
		{
			path: "/projects",
			name: "projects",
			components: {
				default: ProjectsPage
			}
		},
		{
			path: "/integrations",
			name: "integrations",
			// LSP-15604: preserve the admin gate the old `settings-app-store` route carried.
			// The sidebar `shouldShow` only hides the nav item; this meta is what stops a
			// non-admin reaching AppStorePage via direct URL or the /apps + /settings/app-store
			// redirects (which resolve here and inherit this check in the beforeEach guard).
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"]
			},
			components: {
				default: AppStorePage
			}
		},
		// Back-compat redirect: /apps was the old top-level path before LSP-15604
		// promoted this area to the "Integrations" sidebar item. Return an object
		// (not a string) so query + hash survive the redirect — Vue Router 4 drops
		// both on a plain-string redirect.
		{
			path: "/apps",
			redirect: (to) => ({ path: "/integrations", query: to.query, hash: to.hash })
		},
		{
			// LSP-19475: "Spark" is the one customer-facing noun for this feature, so /sparks is the
			// canonical route and /templates stays as an alias so existing bookmarks and links keep
			// resolving. Do not remove the alias — it is the whole reason the rename is safe.
			path: "/sparks",
			alias: "/templates",
			name: "templates",
			component: TemplatesPage,
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"]
			}
		},
		{
			path: "/reports",
			name: "reports",
			components: {
				default: ReportsPage
			},
			// Feature-flagged: block direct navigation to /reports when disabled. Returns a value
			// (true / redirect) rather than the deprecated next() callback — see LSP-15525 contract.
			beforeEnter: () => (isReportsEnabled() ? true : "/")
		},
		{
			// LSP-21829: every org user sees every submitted review, read-only, so there are no requiredRoles.
			// Each row's canOpen is decided by the API with the session / project detail boundaries.
			path: "/reviews",
			name: "reviews",
			components: {
				default: ReviewsPage
			},
			// Follows the API's CUSTOMER_REVIEW_PAGE_ENABLED switch (/me config.featureFlags.customer_review_page). Loads me
			// when a deep link arrives first; a failed load reads as off.
			beforeEnter: async () => ((await loadMeFeatureFlag("customer_review_page")) ? true : "/")
		},
		{
			path: "/sparks/new",
			alias: "/templates/new",
			name: "template-new",
			component: TemplateEditPage,
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"],
				orgScoped: true,
				orgListPath: "/sparks"
			}
		},
		{
			path: "/sparks/:id",
			alias: "/templates/:id",
			name: "template-edit",
			component: TemplateEditPage,
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"],
				orgScoped: true,
				orgListPath: "/sparks"
			}
		},
		{
			path: "/sparks/:id/details",
			alias: "/templates/:id/details",
			name: "template-details",
			component: TemplateDetailsPage,
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"],
				orgScoped: true,
				orgListPath: "/sparks"
			}
		},
		{
			path: "/conversations/:id",
			name: "conversation",
			meta: {
				entityTableName: "conversations",
				orgScoped: true,
				orgListPath: "/conversations"
			},
			components: {
				default: EntityDetailPage
			},
			props: (route) => ({
				entityId: route.params.id
			})
		},
		{
			path: "/projects/:id",
			name: "project",
			meta: {
				entityTableName: "projects",
				orgScoped: true,
				orgListPath: "/projects"
			},
			components: {
				default: EntityDetailPage
			},
			props: (route) => ({
				projectId: route.params.id,
				shareId: route.query.shareId
			})
		},
		{
			// LSP-20890: the path-segment share link is an alias of the canonical `?shareId=` form that
			// Project.shareUrl generates. Rendering it directly left signed-out recipients on Auth0 —
			// isShareLinkRoute / isShareLinkPath only recognise the query form, and the old props read a
			// param that did not exist. Record redirects resolve before beforeEach, so every guard and
			// EntityDetailPage's anonymous handling see one shape. The path segment is the link's token,
			// so it wins over a stray `?shareId=`; other query keys and the tab hash are kept.
			path: "/projects/:id/:sharingId",
			name: "project-shared",
			redirect: (to) => ({
				name: "project",
				params: { id: to.params.id },
				query: { ...to.query, shareId: to.params.sharingId },
				hash: to.hash
			})
		},
		{
			path: "/conversations/ical/:id",
			name: "conversation-ical",
			meta: { orgScoped: true, orgListPath: "/conversations" },
			component: HomePage
		},
		{
			path: "/upload",
			name: "upload-photo",
			component: ActionUploadPhoto,
			props: (route) => ({
				conversationType: "sync",
				allowJoin: true,
				showHeader: true,
				projectId: route.query.projectId // Extract `projectId` from the query parameters
			})
		},
		{
			path: "/photo",
			name: "photo",
			component: ActionPhoto,
			props: (route) => ({
				conversationType: "sync",
				allowJoin: true,
				showHeader: true,
				instructionCreateReturnTo: buildInstructionCreateReturnTo("/photo", route.query),
				projectId: route.query.projectId // Extract `projectId` from the query parameters
			})
		},
		{
			path: "/async-recording",
			name: "async-recording",
			component: ActionStartRecording,
			props: (route) => ({
				conversationType: "sync",
				allowJoin: true,
				showHeader: true,
				instructionCreateReturnTo: buildInstructionCreateReturnTo("/async-recording", route.query),
				projectId: route.query.projectId // Extract `projectId` from the query parameters
			})
		},
		{
			path: "/live-call",
			name: "live-call",
			component: ActionVideo,
			props: (route) => ({
				conversationType: "sync",
				allowJoin: true,
				showHeader: true,
				projectId: route.query.projectId // Extract `projectId` from the query parameters
			})
		},
		{
			path: "/schedule",
			name: "schedule",
			component: ActionSchedule,
			props: (route) => ({
				conversationType: "sync",
				allowJoin: true,
				showHeader: true,
				projectId: route.query.projectId // Extract `projectId` from the query parameters
			})
		},
		{
			path: "/request-recording",
			name: "request-recording",
			component: ActionVideo,
			props: (route) => ({
				conversationType: "async",
				allowJoin: false,
				showHeader: true,
				instructionCreateReturnTo: buildInstructionCreateReturnTo("/request-recording", route.query),
				projectId: route.query.projectId // Extract `projectId` from the query parameters
			})
		},
		{
			path: "/notifications",
			name: "notifications",
			component: NotificationsPage
		},
		{
			path: "/search",
			name: "search",
			component: SearchResults
		},
		{
			path: "/search/results",
			name: "search-results",
			component: EntityResultsPage
		},
		{
			path: "/checklist/:id",
			name: "checklist",
			// LSP-16415: the template editor is reached from the admin-only
			// checklist-templates settings page, so it carries the same role gate
			// as that list. The user-facing /checklists page stays ungated.
			meta: {
				orgScoped: true,
				orgListPath: "/checklists",
				requiredRoles: ["Administrator", "Super Administrator", "Support"]
			},
			component: ChecklistEditor
		},
		{
			path: "/instruction/:id",
			name: "instruction",
			// LSP-16168: the editor is reached from the admin-only instruction-templates
			// settings page, so it carries the same role gate as that list.
			meta: {
				orgScoped: true,
				orgListPath: "/settings/instruction-templates",
				requiredRoles: ["Administrator", "Super Administrator", "Support"]
			},
			component: InstructionEditor
		},
		{
			path: "/live-call-form/:id",
			name: "live-call-form",
			// The editor is reached from the admin-only live-call-forms settings page,
			// so it carries the same role gate as that list.
			meta: {
				orgScoped: true,
				orgListPath: "/settings/live-call-forms",
				requiredRoles: ["Administrator", "Super Administrator", "Support"]
			},
			component: LiveCallFormEditor
		},
		{
			path: "/checklists",
			name: "checklists",
			// LSP-20865: this legacy top-level URL has no projectId, while ProjectChecklists
			// requires one. Redirect instead of issuing /projects/undefined/checklists and
			// rendering an empty state alongside a load error.
			redirect: "/projects"
		},
		{
			path: "/forms",
			name: "forms",
			component: FormsPage,
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"]
			}
		},
		{
			path: "/forms/new",
			name: "form-new",
			component: FormPage,
			props: { isNew: true },
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"],
				orgScoped: true,
				orgListPath: "/forms"
			}
		},
		{
			path: "/forms/:id",
			name: "form",
			component: FormPage,
			props: (route) => ({
				formId: route.params.id,
				isNew: false
			}),
			meta: {
				requiredRoles: ["Administrator", "Super Administrator", "Support"],
				orgScoped: true,
				orgListPath: "/forms"
			}
		},
		{
			path: "/videos",
			name: "videos",
			component: VideosPage
		},
		{
			path: "/data-privacy",
			name: "data-privacy",
			component: DataPrivacyPage
		},
		{
			path: "/whats-new",
			name: "whats-new",
			component: WhatsNewPage,
			// Feature-flagged: block direct navigation to /whats-new when disabled. Returns a value
			// (true / redirect) rather than the deprecated next() callback — see LSP-15525 contract.
			beforeEnter: () => (isWhatsNewEnabled() ? true : "/")
		},
		{
			path: "/gallery/:galleryId/:accessToken?",
			redirect: (to) => ({
				path: `/sheet/${to.params.galleryId}${to.params.accessToken ? `/${to.params.accessToken}` : ""}`
			})
		},
		{
			path: "/sheet/:sheetId/:accessToken?",
			name: "sheet-view",
			component: SheetView,
			meta: {
				public: true
			}
		},
		{
			// LSP-17770: per-recipient claim landing page. Public — the recipient may be
			// signed out; ClaimSharePage orchestrates login/signup/claim itself.
			path: "/share/:token",
			name: "share-claim",
			component: ClaimSharePage,
			meta: {
				public: true
			}
		},
		{
			path: "/mobile/uploads",
			name: "mobile-uploads",
			component: MobileUploadsFallback,
			meta: { public: true }
		},
		{
			// LSP-23305: where https://…/mobile/record lands when the app does not open it.
			path: "/mobile/record",
			name: "mobile-record",
			component: MobileRecordFallback,
			meta: { public: true }
		},
		{
			path: "/devtools/uploads",
			name: "devtools-uploads",
			component: DevToolsUploadsPage
		},
		{
			path: "/:pathMatch(.*)*",
			name: "notfound",
			component: HomePage
		}
	]
});
// very basic "setup" of a global guard
router.beforeEach(async (to, from) => {
	const safeSearchQuery = withoutOversizedSearch(to.query);
	if (safeSearchQuery) {
		useToast().warning(`${SEARCH_LENGTH_MESSAGE} Please try a shorter search.`);
		return { path: to.path, query: safeSearchQuery, hash: to.hash, replace: true };
	}
	// LSP-17767 (review, critical): the share-claim route's :token param is a live single-use
	// credential — persisting it raw into sessionStorage.routerHistory would let any XSS or
	// storage-reading extension lift a claimable token (scrubClaimToken guards the telemetry
	// sinks; this is the storage sink). Redact it before the entry is stored; nothing reads
	// the token back out of history (the claim page is a terminal destination).
	const historyParams = to.name === "share-claim" ? { ...to.params, token: "[REDACTED]" } : to.params;
	try {
		addToRouterHistory({ name: to.name, params: historyParams, query: to.query });
	} catch (error) {
		// LSP-17341: optional history must not block navigation, including fresh sign-in.
		logInfo("Router: navigation history unavailable", { errorName: error?.name });
	}

	if (to.name == "callback") {
		// check if "to"-route is "callback" and allow access
		console.log("🔄 Processing Auth0 callback...");
		getAuthInstance()
			.handleAuthentication()
			.then(function (appState) {
				// LSP-17341: recovery may have handed navigation to a fresh Auth0 login.
				// Do not replace that navigation with the normal post-callback destination.
				if (getAuthInstance()._loginRedirectInFlight || getAuthInstance()._restoreRecoveryNavigated) return;
				// Default: land on home (org-scoped pages were flakey post-login if the
				// user picked a different org at the Auth0 prompt). Exception: a private
				// share link the user clicked while logged out — that's signed access via
				// shareId, not org membership, so it's safe to restore.
				const restoredPath = isShareLinkPath(appState?.path) ? appState.path : null;
				// The callback is single-use. Keep the selector's recovery entry behind
				// Home instead of leaving a consumed callback as the next Back target.
				window.location.replace(restoredPath || "/");
			})
			.catch(function (error) {
				console.error("❌ Auth0 callback processing failed:", error);

				if (getAuthInstance().isBlocked()) {
					return;
				}
				console.log("🚪 Redirecting to login after callback failure");
				// AUTOMATIC: the app's own recovery from a failed callback exchange, not a user click.
				const recoveryState = error?.appState?.orgSwitch === true ? error.appState : {};
				getAuthInstance().login(recoveryState, true, null, {}, { isExplicitUserIntent: false });
			});
		return false;
	} else if (to.name === "sign-in") {
		// LSP-17341: this stable entry also handles completed-selector history.
		// Run before the authenticated guard and never clear the existing session.
		logInfo("Router: starting a fresh visible sign-in", {});
		await getAuthInstance().login({}, true, null, {}, { isExplicitUserIntent: true, ignoreQueryParameters: true });
		return false;
	} else if (to.name === "logout") {
		// LSP-16670: /logout is the post-sign-out landing (client.logout() redirects through
		// Auth0's /v2/logout with returnTo=/logout). The generic unauthenticated branch at the
		// bottom of this guard would call login() here — and because the Auth0 SSO cookie can
		// survive /v2/logout, that login silently re-authenticates the user straight back into
		// the app (the "sign-out didn't work" bounce, confirmed in staging RUM: /logout view →
		// /callback?code= → /). Force the Auth0 login screen instead (forceLogin=true sets
		// prompt:"login") so sign-out deterministically ends logged-out regardless of whether
		// the SSO session survived. Handled here, BEFORE the isAuthenticated() branch, so a
		// session that hasn't finished clearing can't route /logout through the authenticated
		// HomePage + getMe path (which would re-establish it) either.
		// EXPLICIT: the user asked to sign out and this is that flow's final leg; dropping it would
		// leave them apparently-signed-out but still authenticated.
		getAuthInstance().login({}, true, null, {}, { isExplicitUserIntent: true });
		return false;
	} else if (to.name.indexOf("conversation-ical") != -1 && (await getAuthInstance().isAuthenticated())) {
		if (to.params.id) {
			const conversationId = to.params.id;
			const conversation = await api.getConversation(conversationId);

			if (conversation) {
				window.location.href = conversation.creatorUrl;
			} else {
				console.warn(`Conversation: Status=NotFound, Id=${conversationId}`);
			}
		}
		return false;
	} else if (await getAuthInstance().isAuthenticated()) {
		// if authenticated allow access
		// redirect some domains...would be nice to do this elsewhere
		// we allow the app to be authenticated, then redirect after
		const redirectFrom = ["app.supbubble.com", "contact.liveswitch.com"];
		const redirectTo = "app.contact.liveswitch.com";
		for (let i = 0; i < redirectFrom.length; i++) {
			// check root url
			if (window.location.host == redirectFrom[i]) {
				let target = new URL("https://" + redirectFrom[i]);
				target.hostname = redirectTo;
				window.location.href = target;
				// bail out of the whole function while page redirects
				return false;
			}
		}

		if (hardNavigateStaleSettingsRoute(to)) {
			return false;
		}

		// check if we're loaded via the api
		if (!isLoaded) {
			let me = null;
			try {
				me = await api.getMe();
				if (!me) {
					// should not happen, as this only runs after auth
				}
			} catch (e) {
				if (isTerminalGetMeAuthError(e)) {
					// AUTOMATIC: the app reacting to a terminal getMe auth failure.
					await getAuthInstance().doDefaultLogin({ isExplicitUserIntent: false });
					return false;
				}
				if (isTransientAuthTokenError(e) || isNetworkShapedBootAccountLoadError(e)) {
					deferredBootAccountRouteFullPath = to.fullPath;
					logInfo("Router beforeEach: transient boot account-load failure; navigation deferred", {
						routeName: to.name,
						path: to.fullPath,
						classification: "transient",
						retryable: true
					});
					return false;
				}
				if (isUnauthenticatedError(e)) {
					let tokenProbeError = null;
					try {
						const token = await getAuthInstance().getAccessToken({ redirectOnAuthFailure: false });
						if (typeof token === "string" && token.trim() !== "") {
							deferredBootAccountRouteFullPath = to.fullPath;
							logInfo(
								"Router beforeEach: client-side unauthenticated error was transient after token probe; navigation deferred",
								{
									routeName: to.name,
									path: to.fullPath,
									classification: "transient",
									retryable: true,
									probeResult: "token_available"
								}
							);
							return false;
						}
					} catch (probeError) {
						tokenProbeError = probeError;
						if (isTransientAuthTokenError(probeError)) {
							deferredBootAccountRouteFullPath = to.fullPath;
							logInfo(
								"Router beforeEach: client-side unauthenticated error was transient after token probe; navigation deferred",
								{
									routeName: to.name,
									path: to.fullPath,
									classification: "transient",
									retryable: true,
									errorCode: probeError?.error,
									errorName: probeError?.name,
									probeResult: "transient_error"
								}
							);
							return false;
						}
					}
					logInfo("Router beforeEach: client-side unauthenticated error was terminal after token probe; redirecting to login", {
						routeName: to.name,
						path: to.fullPath,
						classification: "terminal",
						retryable: false,
						errorCode: tokenProbeError?.error,
						errorName: tokenProbeError?.name
					});
					// AUTOMATIC: the app reacting to a terminal token probe, not a user click.
					await getAuthInstance().doDefaultLogin({ isExplicitUserIntent: false });
					return false;
				}
				// 404 Error means some data wasn't found. Could be the organization, account or user.
				const noAccountMessage = e?.message ?? e?.error;
				if (e?.errorCode) {
					switch (e.errorCode) {
						case "USER_NOT_FOUND":
							if (isShareLinkRoute(to)) {
								// Cached Auth0 session but no account — let navigation proceed so
								// EntityDetailPage can load the public share view via getProjectInsecure.
								return;
							}
							api.openNoAccountDialog(undefined, noAccountMessage);
							break;
						case "ACCOUNT_NOT_FOUND":
						case "ORGANIZATION_NOT_FOUND":
							api.openNoAccountDialog("org_access_lost", noAccountMessage);
							break;
						default:
							// If it was not a code I was expecting, just log them out.
							await getAuthInstance().doDefaultLogout();
							break;
					}
				} else {
					// If we had some random exception theres a chance our service is down.
					// I don't want to create a infinite loop in this case, just send them back to the login page.
					await getAuthInstance().doDefaultLogout();
				}
				return false;
			}
			isLoaded = true;
			const account = new Account(me.account);
			currentAccount = account;

			if (me) {
				// This is a special extra setup to help track the user.
				// The Datadog user and org are set in api._setDatadogContext when /me loads (LSP-23356).
				if (me.user) {
					// Track the current user (allows seeing multiple errors for a user).
					console.log("User:", { id: me.user.id ?? "", email: me.user.email ?? "", username: me.user.email ?? "" });
				}
			}
			// This will give you information of the last path they went to and came from.
			if (to) console.log("Context to:", to);
			if (from) console.log("Context from:", from);

			// no orgs means we move to the setup
			if (me.organization && me.organization.id) {
				console.log("starting");
				try {
					// AUTOMATIC: nobody asked for this switch — it is boot reconciling the token's
					// organization with me.organization.id, so it must not race a bfcache-restore
					// recovery navigation that already owns this page.
					await api.setOrganizationId(me.organization.id, { isExplicitUserIntent: false });
					// The switch landed — give a later flap in this same tab a fresh retry budget.
					bootOrgSwitchAttempts = 0;
				} catch (orgSwitchError) {
					// LSP-17342: setOrganizationId's redirect fallback REJECTS instead of hanging
					// forever on the freeze that used to follow it, and this call site is not inside
					// the getMe try above, so an unhandled rejection here would escape beforeEach and
					// stall navigation. But the rejection has TWO causes with OPPOSITE correct
					// responses, and treating them alike (github-actions review 5360786614, CRITICAL)
					// silently completed boot on a stale-org access token — every later API call then
					// ran against the WRONG organization with only a background log to show it.
					if (isAuth0RedirectSuppressedError(orgSwitchError)) {
						// A bfcache-restore recovery owns this page's navigation. This used to fall
						// through on the reasoning that "the document is leaving anyway" — but that
						// is NOT guaranteed (review 5023486490 #2): the latch is set before
						// reload()/replace() and a navigation the browser CANCELS rather than
						// refuses never clears it synchronously, so this can be reached in a
						// document that stays. Falling through then completed authenticated boot on
						// the stale-org token in a living document, which is the exact CRITICAL
						// this catch exists to prevent, just reached by a different route. So do
						// not continue: defer and let the replay re-run boot. Suppression is now
						// inherently TRANSIENT because _redirectToAuth0 consumes the latch when it
						// spends it, so the replay's switch is no longer suppressed and will either
						// succeed or classify itself as a genuine failure below. If the document
						// really was leaving, deferring costs nothing — it is torn down either way.
						logInfo("Router: org switch during authenticated boot was suppressed by a bfcache-restore recovery; deferring", {
							file: "router/index.js",
							organizationId: me.organization.id,
							routeName: to.name,
							path: to.fullPath,
							classification: "transient",
							retryable: true
						});
						isLoaded = false;
						deferBootOrgSwitchRetry(to, "suppressed-by-restore-recovery", me.organization.id);
						return false;
					} else {
						logError("Router: org switch during authenticated boot failed", orgSwitchError, {
							file: "router/index.js",
							organizationId: me.organization.id,
							routeName: to.name,
							path: to.fullPath
						});
						// A GENUINE failure (network down, Auth0 outage, a denied switch). Nothing is
						// navigating away, so continuing here lands the user on the target route while
						// the access token still carries the PREVIOUS organization — setOrganizationId
						// is only reached when the two differ. Must not continue. `isLoaded` was set
						// above, so clear it: otherwise the retry below skips this whole boot block and
						// the switch is never re-attempted.
						isLoaded = false;
						if (
							isTransientAuthTokenError(orgSwitchError) ||
							isNetworkShapedBootAccountLoadError(orgSwitchError) ||
							// setOrganizationId THROWS instead of resolving when the switch did not
							// happen (no Auth0 client). That is a transient auth-client problem, so
							// defer and replay rather than burning a terminal dialog — but never
							// continue, because continuing is the wrong-organization defect.
							isOrganizationSwitchNotPerformedError(orgSwitchError)
						) {
							// Hold the route so the deep link is not lost, and arm the replay HERE. It
							// is not enough to copy the sibling getMe catch's
							// `deferredBootAccountRouteFullPath = ...; return false`: that sibling works
							// only because getMe ALSO failed inside App.vue's loadBootAccountContext,
							// which sends it down scheduleBootAccountOnlineRetry and gives the stored
							// path a repeating consumer. On THIS path getMe SUCCEEDED, so App.vue takes
							// its success branch and calls retryBootAccountDeferredNavigation() exactly
							// ONCE — and whether that single call happens before or after this
							// assignment is a race. If it loses, nothing is armed and the deep link
							// stays cancelled with no dialog and no retry. deferBootOrgSwitchRetry owns
							// the arming so the recovery is self-owned rather than race-dependent, and
							// BOUNDED so a switch that keeps failing ends at the terminal dialog
							// instead of retrying in silence forever.
							deferBootOrgSwitchRetry(to, "genuine-transient-failure", me.organization.id);
							return false;
						}
						// Terminal: surface it, because nothing else will — this is the branch with
						// no retry left to run, and a cancelled navigation with no dialog is the
						// silent dead end this whole finding is about. Deliberately the same dialog
						// the sibling getMe catch uses for ORGANIZATION_NOT_FOUND rather than
						// doDefaultLogin(), because a re-login would land back on this same failing
						// switch and loop; its "Log In Again" / "Log Out" actions are both useful
						// here. The copy is explicit and user-facing — passing the raw Auth0 error
						// message through would show the user transport-level text.
						api.openNoAccountDialog(
							"org_access_lost",
							"We couldn't switch you to your organization, so we've stopped rather than show you another organization's data. Please log in again."
						);
						return false;
					}
				}
				// A single time operation to setup the default country code for organizations.
				if (!me?.organization?.defaultCountryCode) {
					const hasPermissionToUpdateOrg = account.hasPermissionTo("UPDATE_ORGANIZATION");
					if (hasPermissionToUpdateOrg && me?.organization) {
						// Will grab their country code from the service.
						me.organization.defaultCountryCode = await getCountryFromService();
						if (me?.organization?.defaultCountryCode) {
							// Lets update the default value for their organization based on the above value.
							api.updateOrganization(me.organization.id, {
								defaultCountryCode: me.organization.defaultCountryCode
							})
								.then((x) => {
									if (me.organization) {
										me.organization.defaultCountryCode = x.defaultCountryCode;
									}
								})
								.catch((err) => {
									console.error("Failed to update organization country code", err);
								});
						}
					}
				}
				// This will add their default country to the preferred country list in the telephone dropdown.
				if (me.organization?.defaultCountryCode) {
					setDefaultPhoneCountry(me.organization.defaultCountryCode);
				}
				// router.app.$root.$organization = me.organization;

				//StorageWrapper.setItem("org", JSON.stringify(me.organization));
				//const params = new Proxy(new URLSearchParams(window.location.search), {
				//    get: (searchParams, prop) => searchParams.get(prop),
				//  });

				try {
					/*  
						Connect to the socket to receive live updates.
						It is not criticial to the functionality of Contact, so if it fails it fails.
					*/
					if (!Socket.connected) {
						Socket.auth = {
							userId: me.user.id,
							organizationId: me.organization.id
						};
						Socket.connect();
					}
				} catch (e) {
					console.error(e);
				}
			} else {
				api.openNoAccountDialog(me.organizations?.length > 0 ? "org_access_lost" : undefined, me?.message);
				return false;
			}
		} else {
			console.log("routing");
		}

		if (hardNavigateStaleSettingsRoute(to)) {
			return false;
		}

		// Check required roles on EVERY navigation (not just first load)
		if (to.meta?.requiredRoles) {
			if (!currentAccount) {
				return { name: "home" };
			}

			const isSingleRole = typeof to.meta.requiredRoles === "string";
			const hasRole = isSingleRole
				? currentAccount.hasRole(to.meta.requiredRoles)
				: to.meta.requiredRoles.some((role) => currentAccount.hasRole(role));

			if (!hasRole) {
				const rolesString = isSingleRole
					? to.meta.requiredRoles + "s"
					: to.meta.requiredRoles.map((role) => role + "s").join(" or ");
				console.error(`Only ${rolesString} can access this page.`);
				return { name: "home" };
			}
		}
	} else if (to.name === "sheet-view" || to.name === "share-claim" || to.name === "mobile-uploads" || to.name === "mobile-record") {
		// Public/unauthenticated pages — allow access. ClaimSharePage (share-claim)
		// drives its own login/signup/claim flow for signed-out recipients.
		return;
	} else if (isShareLinkRoute(to)) {
		// Show the insecure project page for unauthenticated share-link visitors.
		// LSP-20530: parse with the URL API so a tab hash (`#documents`) never ends up inside the share id.
		const { projectId, shareId } = parseShareLinkLocation(to.fullPath);
		try {
			const project = await api.getProjectInsecure(projectId, shareId);
			if (project) {
				return;
			} else {
				// Preserve the share path through Auth0 — the callback handler will
				// validate it against isShareLinkPath and restore it after login.
				// EXPLICIT: the user opened a share link and needs to sign in to see it.
				getAuthInstance().login({ path: to.fullPath }, false, null, {}, { isExplicitUserIntent: true });
				return false;
			}
		} catch (e) {
			console.error("Failed to load project:", e);
		}
	} else {
		// EXPLICIT: the user navigated to a protected route and is asking to get in.
		// Users always return to home after login — see callback handler above.
		getAuthInstance().login({}, false, null, {}, { isExplicitUserIntent: true });
		return false;
	}
});

/* Capture and log router errors.  */
router.onError((error, to) => {
	try {
		logError("Router Error", error, { file: "router/index.js", to });
	} catch (e) {
		console.warn("error while capturing exception: ", e);
	}
	if (error.message.includes("Failed to fetch dynamically imported module") || error.message.includes("Failed to load module script")) {
		// The error is already captured above. Reload immediately in every environment so
		// customers and local developers are not blocked behind a native browser alert.
		window.location.reload();
	}
});

export default router;
